import { Card, CardColor, CompartmentCard } from "@/src/components/cards";
import { FetchableList } from "@/src/components/fetchableLists";
import {
  CardType,
  FieldOption,
  FolderItem,
  Patient
} from "@/src/data/allTypes";
import { USER } from "@/src/data/globalVariables";
import apiFetch from "@/src/utils/apiFetch";
import { getItemId, uid } from "@/src/utils/utils";
import moment from "moment";
import { useState } from "react";
import { openDrawer } from "../../components/dialogs";
import { MesureChartDrawer } from "../patients/mesuresChart";
import {
  openMesureAndScoresInputs
} from "../patients/mesuresInputs";
import { PatientLastScoresList } from "./scoresCompartment";

function MesuresCompartment({
  color,
  patient,
  parent,
}: {
  color?: CardType;
  patient: Patient;
  parent: FolderItem;
}) {
  const [reload, setReload] = useState(0);
  return (
    <CompartmentCard
      title={"Mesures"}
      color={color || "red"}
      onClick={async () => {
        await openMesureAndScoresInputs({
          patient,
          parent,
          onConfirm: async (mesures) => {
            setReload(reload + 1);
          },
        });
      }}
    >
      <PatientLastMesuresList
        key={`patientMesuresList${reload}`}
        type={color}
        patient={patient}
        parent={parent}
      ></PatientLastMesuresList>
    </CompartmentCard>
  );
}

function MesuresAndScoresCompartment({
  patient,
  parent,
  color,
}: {
  patient: Patient;
  parent: FolderItem;
  color?: CardColor;
}) {
  const [reload, setReload] = useState(0);
  return (
    <CompartmentCard
      title={"Mesures et scores"}
      color={color || "red"}
      onClick={async () => {
        await openMesureAndScoresInputs({
          patient,
          parent,
          onConfirm: async (mesures) => {
            setReload(reload + 1);
          },
        });
      }}
    >
      <div className="c1 gap15">
        <PatientLastMesuresList
          key={`patientMesuresList${reload}`}
          patient={patient}
          parent={parent}
          type={color}
        ></PatientLastMesuresList>
        <PatientLastScoresList
          key={`PatientLastScoresList${reload}`}
          patient={patient}
          parent={null}
          type={color}
        ></PatientLastScoresList>
      </div>
    </CompartmentCard>
  );
}

function PatientLastMesuresList({
  type,
  parent,
  patient,
}: {
  type?: CardType;
  parent: FolderItem | null | undefined;
  patient: Patient;
}) {
  const [reload, setReload] = useState(0);
  let [searchKey, setSearchKey] = useState("");

  function getValue(
    value: any,
    options: (FieldOption | null | undefined)[]
  ): any {
    if (Array.isArray(value)) return value.map((v) => getValue(v, options));
    let val = value?.id || value;
    let option = options?.find((option) => option?.id === val);
    return option?.name || val;
  }

  return (
    <FetchableList
      queryKey={["PatientLastMesuresList", searchKey, patient.id, reload]}
      queryFn={() =>
        apiFetch(
          "patientMesures",
          "loadPatientLastMesures",
          { patientId: getItemId(patient), parentId: getItemId(parent) },
          "Erreur lors du chargement des mesures"
        )
      }
    >
      {(data) => (
        <div className="grid c1 gap10">
          {data?.map((mesure) => {
            return (
              <Card
                key={uid()}
                color={type || "red"}
                title={mesure.reference?.name}
                leading={<></>}
                trailing={
                  <div key={uid()}>
                    {mesure.fieldAnswers?.map((answer) => {
                      return (
                        <div key={uid()} className="bold tright">
                          {/* <span>{answer.field?.name || ""}</span>  */}
                          {getValue(
                            answer?.value,
                            answer?.field?.options || []
                          )}{" "}
                          <span className="gray normal">
                            {answer?.field?.unit?.name || ""}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                }
                onClick={async () => {
                  if (!mesure.reference) return;
                  let isWeight = mesure.reference.name
                    ?.toLowerCase()
                    .includes("poids");
                  let isHeight = mesure.reference.name
                    ?.toLowerCase()
                    .includes("taille");

                  let weightReference =
                    USER.get()?.speciality?.frequentReferences?.find((ref) =>
                      ref?.name?.toLowerCase().includes("poids")
                    );
                  let heightReference =
                    USER.get()?.speciality?.frequentReferences?.find((ref) =>
                      ref?.name?.toLowerCase().includes("taille")
                    );

                  await openDrawer({
                    id: "mesuresChartDrawer",
                    title: "Historique des mesures",
                    additionalClasses: ["medium"],
                    content: (
                      <div className="c1 gap10">
                        <MesureChartDrawer
                          parent={parent}
                          patient={patient}
                          reference={mesure.reference}
                          additionalHtml={
                            <div>
                              {isWeight && heightReference && (
                                <div>
                                  <MesureChartDrawer
                                    parent={parent}
                                    patient={patient}
                                    reference={heightReference}
                                    showMesuresList={false}
                                  />
                                </div>
                              )}
                              {isHeight && weightReference && (
                                <div>
                                  <MesureChartDrawer
                                    parent={parent}
                                    patient={patient}
                                    reference={weightReference}
                                    showMesuresList={false}
                                  />
                                </div>
                              )}
                            </div>
                          }
                        />
                      </div>
                    ),
                  });
                }}
              >
                <div className="gray">{moment(mesure._date).fromNow()}</div>
              </Card>
            );
          })}
        </div>
      )}
    </FetchableList>
  );
}

export { MesuresAndScoresCompartment, MesuresCompartment };
